import {
  Handshake,
  LucideIcon,
  Home,
  Calendar,
  Building2,
  Users,
  Activity,
  FlaskConical,
  FileText,
  Heart,
  TestTube2,
  Pill,
  Utensils,
  Share2,
  TestTubes,
  FileSliders,
  Leaf,
  Dumbbell,
  Syringe,
  Hospital,
  Lightbulb,
  Sparkles,
  Settings,
  Play,
  Brain,
  ListChecks,
  CheckSquare,
  Mic,
  Stethoscope,
  BookOpen,
} from 'lucide-react';
import { NAVIGATION_ITEM_PERMISSIONS } from '@/config/navigation-permission-registry';
import { LEADS_ALLOWED_ROLES, LEADS_WRITE_ROLES } from '@/pages/leads/leadConstants';
import { EARLY_HUB_ALLOWED_ROLES } from '@/pages/pmx-early-ops/earlyHubConstants';
import type { NavigationPermissionRequirement } from '@/types/navigation-permissions';
import type { PermissionAction, PermissionFeature } from '@/utils/permissions';
import { matchesRoleAllowList } from '@/utils/roles';

export interface NavigationItem {
  id: string;
  title: string;
  description?: string;
  path: string;
  category: 'page' | 'client' | 'tab' | 'section';
  keywords: string[];
  icon: LucideIcon;
  /** Prefer permissions when a registry entry exists. */
  requiredPermission?: NavigationPermissionRequirement;
  /** Fallback role gate for entries without a permission registry mapping. */
  requiredRoles?: string[];
  metadata?: {
    tabId?: string;
    sectionId?: string;
    clientRequired?: boolean;
    parentPage?: string;
  };
}

export const navigationIndex: NavigationItem[] = [
  // Main Pages
  {
    id: 'hub',
    requiredPermission: NAVIGATION_ITEM_PERMISSIONS['hub'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Hub',
    description: 'Main dashboard and overview',
    path: '/hub',
    category: 'page',
    keywords: ['home', 'dashboard', 'main', 'overview', 'start'],
    icon: Home,
  },
  {
    id: 'early-hub',
    title: 'Early Hub',
    description: 'PMX Early client roster and 11-stage journey tracker',
    path: '/hub/early',
    category: 'page',
    keywords: ['early', 'pmx', 'ops', 'roster', 'journey', 'tracker', 'blood', 'thrive'],
    icon: Share2,
    requiredRoles: [...EARLY_HUB_ALLOWED_ROLES],
  },
  {
    id: 'appointments',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['appointments'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Appointments',
    description: 'View and manage appointments',
    path: '/appointments',
    category: 'page',
    keywords: ['schedule', 'calendar', 'booking', 'appointment', 'slots'],
    icon: Calendar,
  },
  {
    id: 'consultation-dashboard',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['consultation-dashboard'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Bookings Dashboard',
    description: 'Consultations, in-centre visits and therapy bookings: mark attendance, outcomes',
    path: '/bookings-dashboard',
    category: 'page',
    keywords: [
      'consultation',
      'booking',
      'attendance',
      'no-show',
      'mark complete',
      'in-centre',
      'therapy',
      'resource',
      'doctor',
    ],
    icon: Stethoscope,
  },
  {
    id: 'leads',
    title: 'Discovery',
    description: 'Discovery-call leads and conversation notes before conversion',
    path: '/leads',
    category: 'page',
    keywords: [
      'discovery',
      'lead',
      'leads',
      'discovery call',
      'prospect',
      'sales',
      'convert',
      'pipeline',
    ],
    icon: Handshake,
    requiredRoles: LEADS_ALLOWED_ROLES,
  },
  {
    id: 'new-lead',
    title: 'New lead',
    description: 'Create a discovery lead and paste call notes',
    path: '/leads/new',
    category: 'page',
    keywords: ['new lead', 'add lead', 'create lead', 'discovery notes'],
    icon: Handshake,
    requiredRoles: LEADS_WRITE_ROLES,
  },
  {
    id: 'page-recordings',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['page-recordings'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Voice recordings',
    description: 'Consultation and page voice notes with transcripts',
    path: '/page-recordings',
    category: 'page',
    keywords: ['voice', 'recording', 'transcript', 'page recording', 'consultation note', 'audio'],
    icon: Mic,
  },
  {
    id: 'backoffice',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['backoffice'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'BackOffice',
    description: 'Administrative and reference management',
    path: '/backoffice',
    category: 'page',
    keywords: ['admin', 'settings', 'references', 'configuration', 'management'],
    icon: Building2,
  },
  {
    id: 'users',
    requiredPermission: NAVIGATION_ITEM_PERMISSIONS['users'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Users',
    description: 'User management and profiles',
    path: '/users',
    category: 'page',
    keywords: ['user', 'members', 'staff', 'profiles', 'people'],
    icon: Users,
  },
  {
    id: 'business-dashboard',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['business-dashboard'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Business Dashboard',
    description: 'Business program overview',
    path: '/business-dashboard',
    category: 'page',
    keywords: ['business', 'thrive', 'program', 'wellness', 'dashboard'],
    icon: Activity,
  },
  {
    id: 'studio',
    requiredPermission: NAVIGATION_ITEM_PERMISSIONS['studio'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Studio',
    description: 'Clinical templates and protocols',
    path: '/studio',
    category: 'page',
    keywords: ['templates', 'protocols', 'clinical', 'forms', 'studio'],
    icon: FlaskConical,
  },
  {
    id: 'discovery',
    requiredPermission: NAVIGATION_ITEM_PERMISSIONS['discovery'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Client Discovery',
    description: 'Client discovery and assessment',
    path: '/discovery',
    category: 'page',
    keywords: ['discovery', 'assessment', 'client', 'patient', 'intake'],
    icon: FileText,
  },

  // BackOffice Sections
  {
    id: 'backoffice-organization',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['backoffice-organization'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Orgs & Partners',
    description: 'Clinics, departments, and rooms',
    path: '/backoffice?view=organization-references',
    category: 'section',
    keywords: ['organization', 'clinic', 'department', 'room', 'facility', 'partner'],
    icon: Building2,
    metadata: {
      sectionId: 'organization-references',
      parentPage: 'BackOffice',
    },
  },
  {
    id: 'backoffice-clinical',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['backoffice-clinical'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Clinical',
    description: 'Clinical thresholds, ranges, and pathways',
    path: '/backoffice?view=clinical-references',
    category: 'section',
    keywords: ['clinical', 'threshold', 'range', 'pathway', 'data', 'limits'],
    icon: Heart,
    metadata: {
      sectionId: 'clinical-references',
      parentPage: 'BackOffice',
    },
  },
  {
    id: 'backoffice-medical',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['backoffice-medical'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Medical',
    description: 'Tests, medicines, and supplements',
    path: '/backoffice?view=medical-references',
    category: 'section',
    keywords: ['medical', 'test', 'medicine', 'supplement', 'drug', 'medication'],
    icon: Pill,
    metadata: {
      sectionId: 'medical-references',
      parentPage: 'BackOffice',
    },
  },
  {
    id: 'backoffice-lifestyle',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['backoffice-lifestyle'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Lifestyle',
    description: 'Diet plans, exercises, and activities',
    path: '/backoffice?view=lifestyle-references',
    category: 'section',
    keywords: ['lifestyle', 'diet', 'exercise', 'nutrition', 'fitness', 'wellness'],
    icon: Leaf,
    metadata: {
      sectionId: 'lifestyle-references',
      parentPage: 'BackOffice',
    },
  },
  {
    id: 'backoffice-membership',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['backoffice-membership'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Membership',
    description: 'Memberships and add-ons',
    path: '/backoffice?view=membership-references',
    category: 'section',
    keywords: ['membership', 'service', 'program', 'addon', 'subscription'],
    icon: Syringe,
    metadata: {
      sectionId: 'membership-references',
      parentPage: 'BackOffice',
    },
  },

  // Client Details Tabs
  {
    id: 'client-journey',
    title: 'Client Journey',
    description: 'Member journey timeline and completion status',
    path: '/client/:clientId/details?tab=journey',
    category: 'tab',
    keywords: ['journey', 'timeline', 'onboarding', 'activity', 'completion'],
    icon: Activity,
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-journey'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    metadata: {
      tabId: 'journey',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-visits',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-visits'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Client Visits',
    description: 'View and manage client visits',
    path: '/client/:clientId/details?tab=visits',
    category: 'tab',
    keywords: ['visit', 'appointment', 'consultation', 'session'],
    icon: Calendar,
    metadata: {
      tabId: 'visits',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-appointments',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-appointments'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Client Appointments',
    description: 'Scheduled appointments',
    path: '/client/:clientId/details?tab=appointments',
    category: 'tab',
    keywords: ['appointment', 'schedule', 'booking', 'calendar'],
    icon: Calendar,
    metadata: {
      tabId: 'appointments',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-clinical',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-clinical'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Clinical Information',
    description: 'Clinical data and thresholds',
    path: '/client/:clientId/details?tab=clinical',
    category: 'tab',
    keywords: ['clinical', 'medical', 'health', 'threshold', 'vital'],
    icon: Heart,
    metadata: {
      tabId: 'clinical',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-clinical-notes',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-clinical'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Clinical Notes',
    description: 'Patient-visible clinical notes and addenda',
    path: '/client/:clientId/details?tab=clinical_notes',
    category: 'tab',
    keywords: ['clinical', 'notes', 'note', 'addendum', 'birthday'],
    icon: Heart,
    metadata: {
      tabId: 'clinical_notes',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-journal',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-journal'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Client Nutrition',
    description: 'Nutrition entries and client lifestyle context',
    path: '/client/:clientId/details?tab=nutrition',
    category: 'tab',
    keywords: ['nutrition', 'journal', 'notes', 'observation', 'record', 'log'],
    icon: FileText,
    metadata: {
      tabId: 'journal',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-programs',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-programs'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Client Programs',
    description: 'Enrolled programs and offerings',
    path: '/client/:clientId/details?tab=offerings',
    category: 'tab',
    keywords: ['program', 'offering', 'enrollment', 'package', 'service'],
    icon: Sparkles,
    metadata: {
      tabId: 'offerings',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-tests',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-tests'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Client Tests',
    description: 'Laboratory and diagnostic tests',
    path: '/client/:clientId/details?tab=test_enrollments',
    category: 'tab',
    keywords: ['test', 'lab', 'laboratory', 'diagnostic', 'result'],
    icon: TestTube2,
    metadata: {
      tabId: 'test_enrollments',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-questionnaires',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-questionnaires'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Client Questionnaires',
    description: 'Forms and assessments',
    path: '/client/:clientId/details?tab=questionnaires',
    category: 'tab',
    keywords: ['questionnaire', 'form', 'assessment', 'survey', 'intake'],
    icon: FileSliders,
    metadata: {
      tabId: 'questionnaires',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-biomarkers',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-biomarkers'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Biomarker Analysis',
    description: 'Biomarker trends and analysis',
    path: '/client/:clientId/details?tab=biomarker_analysis',
    category: 'tab',
    keywords: ['biomarker', 'analysis', 'trend', 'chart', 'graph'],
    icon: Activity,
    metadata: {
      tabId: 'biomarker_analysis',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  // {
  //   id: 'client-biomarker-values',
  //   title: 'Biomarker Values',
  //   description: 'Raw biomarker data',
  //   path: '/client/:clientId/details?tab=biomarker_values',
  //   category: 'tab',
  //   keywords: ['biomarker', 'value', 'data', 'measurement', 'reading'],
  //   icon: TestTubes,
  //   //   metadata: {
  //     tabId: 'biomarker_values',
  //     clientRequired: true,
  //     parentPage: 'Client Details'
  //   }
  // },
  {
    id: 'client-reports',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-reports'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Client Reports',
    description: 'Medical and diagnostic reports',
    path: '/client/:clientId/details?tab=reports',
    category: 'tab',
    keywords: ['report', 'document', 'file', 'pdf', 'result'],
    icon: FileText,
    metadata: {
      tabId: 'reports',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-fitness-assessment',
    title: 'Fitness Assessment',
    description: 'Enter fitness test scores and upload the sheet to Reports',
    path: '/client/:clientId/details?tab=fitness_assessment',
    category: 'tab',
    keywords: ['fitness', 'assessment', 'score', 'excel', 'coach', 'exercise'],
    icon: Dumbbell,
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-fitness-assessment'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    metadata: {
      tabId: 'fitness_assessment',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-pmx-reports',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-pmx-reports'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'PMX Reports',
    description: 'PMX analysis reports',
    path: '/client/:clientId/details?tab=pmx_reports',
    category: 'tab',
    keywords: ['pmx', 'report', 'analysis', 'diagnostic'],
    icon: FileText,
    metadata: {
      tabId: 'pmx_reports',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-thrive-intelligence',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-thrive-intelligence'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Thrive Intelligence',
    description: 'SuperClinic codes and ThriveIQ protocol evaluation',
    path: '/client/:clientId/details?tab=thrive_intelligence',
    category: 'tab',
    keywords: ['thrive', 'intelligence', 'code', 'protocol', 'rule', 'evaluation', 'superclinic'],
    icon: Brain,
    metadata: {
      tabId: 'thrive_intelligence',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },
  {
    id: 'client-sift',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['client-sift'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'SIFT Analysis',
    description: 'SIFT diagnostic analysis',
    path: '/client/:clientId/details?tab=sift',
    category: 'tab',
    keywords: ['sift', 'analysis', 'diagnostic', 'assessment'],
    icon: Share2,
    metadata: {
      tabId: 'sift',
      clientRequired: true,
      parentPage: 'Client Details',
    },
  },

  // Studio Tabs
  {
    id: 'studio-playground',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['studio-playground'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Studio Playground',
    description: 'Test and explore AI Studio features',
    path: '/studio?tab=playground',
    category: 'tab',
    keywords: ['playground', 'test', 'explore', 'experiment', 'ai'],
    icon: FlaskConical,
    metadata: {
      tabId: 'playground',
      parentPage: 'Studio',
    },
  },
  {
    id: 'studio-thrive-iq',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['studio-thrive-iq'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Thrive IQ',
    description: 'Configure business rules and logic',
    path: '/studio?tab=thrive-iq',
    category: 'tab',
    keywords: ['thrive', 'iq', 'rules', 'logic', 'engine', 'ai'],
    icon: Brain,
    metadata: {
      tabId: 'thrive-iq',
      parentPage: 'Studio',
    },
  },
  {
    id: 'studio-biomarker-ranges',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['studio-biomarker-ranges'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Studio Biomarker Ranges',
    description: 'Manage biomarker reference ranges and rules',
    path: '/studio?tab=biomarker-ranges',
    category: 'tab',
    keywords: ['biomarker', 'range', 'reference', 'rule', 'threshold'],
    icon: Activity,
    metadata: {
      tabId: 'biomarker-ranges',
      parentPage: 'Studio',
    },
  },
  {
    id: 'studio-biomarkers',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['studio-biomarkers'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Studio Biomarkers',
    description: 'Manage biomarkers and their configurations',
    path: '/studio?tab=biomarkers',
    category: 'tab',
    keywords: ['biomarker', 'marker', 'configuration', 'test'],
    icon: TestTube2,
    metadata: {
      tabId: 'biomarkers',
      parentPage: 'Studio',
    },
  },
  {
    id: 'studio-clinical-templates',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['studio-clinical-templates'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Clinical Templates',
    description: 'Manage clinical templates and protocols',
    path: '/studio?tab=clinical-templates',
    category: 'tab',
    keywords: ['template', 'clinical', 'form', 'protocol'],
    icon: FileText,
    metadata: {
      tabId: 'clinical-templates',
      parentPage: 'Studio',
    },
  },

  // Thrive IQ Subtabs
  {
    id: 'thrive-iq-rules',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['thrive-iq-rules'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Thrive IQ Rule Management',
    description: 'Create and manage Thrive IQ rules',
    path: '/studio?tab=thrive-iq&subtab=rules',
    category: 'section',
    keywords: ['thrive', 'iq', 'rule', 'management', 'create', 'edit', 'logic'],
    icon: Settings,
    metadata: {
      tabId: 'thrive-iq',
      sectionId: 'rules',
      parentPage: 'Studio > Thrive IQ',
    },
  },
  {
    id: 'thrive-iq-evaluation',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['thrive-iq-evaluation'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Thrive IQ Rule Evaluation',
    description: 'Evaluate rules for clients',
    path: '/studio?tab=thrive-iq&subtab=evaluation',
    category: 'section',
    keywords: ['thrive', 'iq', 'rule', 'evaluation', 'evaluate', 'test', 'client', 'patient'],
    icon: Play,
    metadata: {
      tabId: 'thrive-iq',
      sectionId: 'evaluation',
      parentPage: 'Studio > Thrive IQ',
    },
  },

  // Catalog & Codes Section
  {
    id: 'backoffice-references',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['backoffice-references'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Catalog & Codes',
    description: 'Catalog and registry management',
    path: '/backoffice?view=references',
    category: 'section',
    keywords: ['superclinic', 'catalog', 'registry', 'code'],
    icon: FileSliders,
    metadata: {
      sectionId: 'references',
      parentPage: 'BackOffice',
    },
  },

  // SuperClinic References Tabs
  {
    id: 'superclinic-catalog',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['superclinic-catalog'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'SuperClinic Catalog',
    description: 'Manage catalog entries',
    path: '/backoffice?view=references&tab=catalog',
    category: 'tab',
    keywords: ['catalog', 'superclinic', 'entry', 'reference'],
    icon: FileSliders,
    metadata: {
      tabId: 'catalog',
      sectionId: 'references',
      parentPage: 'BackOffice > SuperClinic References',
    },
  },
  {
    id: 'superclinic-registry',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['superclinic-registry'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'SuperClinic Registry',
    description: 'Manage code registry',
    path: '/backoffice?view=references&tab=registry',
    category: 'tab',
    keywords: ['registry', 'code', 'superclinic', 'reference'],
    icon: FileSliders,
    metadata: {
      tabId: 'registry',
      sectionId: 'references',
      parentPage: 'BackOffice > SuperClinic References',
    },
  },

  // Clinical References Tabs
  {
    id: 'clinical-biomarkers',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['clinical-biomarkers'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Biomarkers',
    description: 'Manage biomarker definitions',
    path: '/backoffice?view=clinical-references&tab=biomarkers',
    category: 'tab',
    keywords: ['biomarker', 'marker', 'clinical', 'test', 'lab'],
    icon: Activity,
    metadata: {
      tabId: 'biomarkers',
      sectionId: 'clinical-references',
      parentPage: 'BackOffice > Clinical References',
    },
  },
  {
    id: 'clinical-biomarker-ranges',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['clinical-biomarker-ranges'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Biomarker Ranges',
    description: 'Normal ranges for biomarkers',
    path: '/backoffice?view=clinical-references&tab=biomarker-ranges',
    category: 'tab',
    keywords: ['range', 'normal', 'reference', 'biomarker', 'clinical', 'threshold'],
    icon: Activity,
    metadata: {
      tabId: 'biomarker-ranges',
      sectionId: 'clinical-references',
      parentPage: 'BackOffice > Clinical References',
    },
  },
  {
    id: 'clinical-pathways',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['clinical-pathways'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Clinical Pathways',
    description: 'Treatment pathways and protocols',
    path: '/backoffice?view=clinical-references&tab=pathways',
    category: 'tab',
    keywords: ['pathway', 'protocol', 'treatment', 'clinical', 'guideline'],
    icon: Share2,
    metadata: {
      tabId: 'pathways',
      sectionId: 'clinical-references',
      parentPage: 'BackOffice > Clinical References',
    },
  },
  {
    id: 'clinical-signs',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['clinical-signs'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Clinical Signs',
    description: 'Manage clinical signs',
    path: '/backoffice?view=clinical-references&tab=signs',
    category: 'tab',
    keywords: ['sign', 'clinical', 'symptom', 'observation'],
    icon: Activity,
    metadata: {
      tabId: 'signs',
      sectionId: 'clinical-references',
      parentPage: 'BackOffice > Clinical References',
    },
  },
  {
    id: 'clinical-symptoms',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['clinical-symptoms'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Clinical Symptoms',
    description: 'Manage clinical symptoms',
    path: '/backoffice?view=clinical-references&tab=symptoms',
    category: 'tab',
    keywords: ['symptom', 'clinical', 'complaint', 'patient'],
    icon: Activity,
    metadata: {
      tabId: 'symptoms',
      sectionId: 'clinical-references',
      parentPage: 'BackOffice > Clinical References',
    },
  },
  {
    id: 'clinical-diagnoses',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['clinical-diagnoses'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Clinical Diagnoses',
    description: 'Manage diagnoses',
    path: '/backoffice?view=clinical-references&tab=diagnoses',
    category: 'tab',
    keywords: ['diagnosis', 'diagnoses', 'clinical', 'condition', 'disease'],
    icon: FileSliders,
    metadata: {
      tabId: 'diagnoses',
      sectionId: 'clinical-references',
      parentPage: 'BackOffice > Clinical References',
    },
  },
  {
    id: 'clinical-templates-backoffice',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['clinical-templates-backoffice'] ??
      NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Clinical Templates (BackOffice)',
    description: 'Manage clinical templates',
    path: '/backoffice?view=clinical-references&tab=templates',
    category: 'tab',
    keywords: ['template', 'clinical', 'form', 'protocol'],
    icon: FileText,
    metadata: {
      tabId: 'templates',
      sectionId: 'clinical-references',
      parentPage: 'BackOffice > Clinical References',
    },
  },
  {
    id: 'clinical-citations-backoffice',
    title: 'Citations (BackOffice)',
    description: 'Manage clinical citations and source documents',
    path: '/backoffice?view=clinical-references&tab=citations',
    category: 'tab',
    keywords: ['citation', 'source', 'reference', 'pubmed', 'doi', 'evidence'],
    icon: BookOpen,
    requiredRoles: ['DOCTOR', 'ADMIN', 'BUSINESS_DEVELOPMENT', 'SUPER_ADMIN'],
    metadata: {
      tabId: 'citations',
      sectionId: 'clinical-references',
      parentPage: 'BackOffice > Clinical References',
    },
  },

  // Workflow Section
  {
    id: 'backoffice-workflow',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['backoffice-workflow'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Workflow',
    description: 'Tasks, checklists, and templates',
    path: '/backoffice?view=workflow-references',
    category: 'section',
    keywords: ['workflow', 'task', 'checklist', 'template', 'process', 'automation'],
    icon: ListChecks,
    metadata: {
      sectionId: 'workflow-references',
      parentPage: 'BackOffice',
    },
  },

  // Workflow References Tabs
  {
    id: 'workflow-tasks',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['workflow-tasks'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Workflow Tasks',
    description: 'Manage reusable workflow tasks',
    path: '/backoffice?view=workflow-references&tab=tasks',
    category: 'tab',
    keywords: ['task', 'workflow', 'todo', 'action', 'step', 'process'],
    icon: CheckSquare,
    metadata: {
      tabId: 'tasks',
      sectionId: 'workflow-references',
      parentPage: 'BackOffice > Workflow References',
    },
  },
  {
    id: 'workflow-checklists',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['workflow-checklists'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Workflow Checklists',
    description: 'Manage reusable workflow checklists',
    path: '/backoffice?view=workflow-references&tab=checklists',
    category: 'tab',
    keywords: ['checklist', 'workflow', 'list', 'template', 'process', 'procedure'],
    icon: ListChecks,
    metadata: {
      tabId: 'checklists',
      sectionId: 'workflow-references',
      parentPage: 'BackOffice > Workflow References',
    },
  },
  {
    id: 'workflow-templates',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['workflow-templates'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Workflow Templates',
    description: 'Manage workflow template library',
    path: '/backoffice?view=workflow-references&tab=templates',
    category: 'tab',
    keywords: ['template', 'workflow', 'library', 'reusable', 'pattern'],
    icon: FileSliders,
    metadata: {
      tabId: 'templates',
      sectionId: 'workflow-references',
      parentPage: 'BackOffice > Workflow References',
    },
  },

  // Workflow Actions
  {
    id: 'create-workflow-task',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['create-workflow-task'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Create New Task',
    description: 'Create a new reusable workflow task',
    path: '/backoffice/create-task',
    category: 'page',
    keywords: ['create', 'new', 'task', 'workflow', 'add', 'todo'],
    icon: CheckSquare,
    metadata: {
      parentPage: 'BackOffice > Workflow References',
    },
  },
  {
    id: 'create-workflow-checklist',
    requiredPermission:
      NAVIGATION_ITEM_PERMISSIONS['create-workflow-checklist'] ?? NAVIGATION_ITEM_PERMISSIONS.hub,
    title: 'Create New Checklist',
    description: 'Create a new reusable workflow checklist',
    path: '/backoffice/create-checklist',
    category: 'page',
    keywords: ['create', 'new', 'checklist', 'workflow', 'add', 'list'],
    icon: ListChecks,
    metadata: {
      parentPage: 'BackOffice > Workflow References',
    },
  },
];

export function filterNavigationIndex(
  items: NavigationItem[],
  hasPermission: (feature: PermissionFeature, action: PermissionAction) => boolean,
  userRole?: string | null
): NavigationItem[] {
  return items.filter((item) => {
    if (item.requiredPermission) {
      const { feature, action } = item.requiredPermission;
      if (!hasPermission(feature, action)) return false;
    }
    if (item.requiredRoles) {
      if (!matchesRoleAllowList(userRole, item.requiredRoles)) return false;
    }
    return true;
  });
}

// Helper function to search navigation items
export function searchNavigationItems(
  query: string,
  hasPermission: (feature: PermissionFeature, action: PermissionAction) => boolean,
  clientId?: string,
  userRole?: string | null
): NavigationItem[] {
  const normalizedQuery = query.toLowerCase().trim();
  const roleFilteredItems = filterNavigationIndex(navigationIndex, hasPermission, userRole);

  if (!normalizedQuery) {
    return roleFilteredItems.slice(0, 10); // Return top 10 items
  }

  // Score each item based on match quality
  const scoredItems = roleFilteredItems.map((item) => {
    let score = 0;
    const lowerTitle = item.title.toLowerCase();
    const lowerDescription = (item.description || '').toLowerCase();

    // Exact title match
    if (lowerTitle === normalizedQuery) score += 100;
    // Title starts with query
    else if (lowerTitle.startsWith(normalizedQuery)) score += 80;
    // Title contains query
    else if (lowerTitle.includes(normalizedQuery)) score += 60;

    // Description contains query
    if (lowerDescription.includes(normalizedQuery)) score += 30;

    // Keyword matches
    item.keywords.forEach((keyword) => {
      const lowerKeyword = keyword.toLowerCase();
      if (lowerKeyword === normalizedQuery) score += 50;
      else if (lowerKeyword.startsWith(normalizedQuery)) score += 30;
      else if (lowerKeyword.includes(normalizedQuery)) score += 20;
    });

    // Process the item path if clientId is needed
    const processedItem =
      clientId && item.metadata?.clientRequired
        ? { ...item, path: item.path.replace(':clientId', clientId) }
        : item;

    return { item: processedItem, score };
  });

  // Filter and sort by score
  return scoredItems
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}
